<script setup lang="ts">
import { buildDisclosureRegulatorSlug } from '~/utils/disclosure-route'

const { localePath, t } = useAppI18n()
const route = useRoute()
const { lock: lockPageScroll, unlock: unlockPageScroll } = useStickyDialogScrollLock()

const routeExchangeAlias = computed(() => {
  const value = route.params.slug
  return Array.isArray(value) ? String(value[0] || '') : String(value || '')
})

interface DisclosureTrader {
  traderCode?: string
  traderName?: string
  englishFullName?: string
  icon?: string
}

interface RegulationDisclosureItem {
  disclosureId?: string
  disclosureAlias?: string
  disclosureType?: string
  disclosureTypeColor?: string
  content?: string
  countryCode?: string
  countryFlag?: string
  countryName?: string
  regulatorName?: string
  disclosureDate?: string
  traders?: DisclosureTrader[]
}

const props = withDefaults(
  defineProps<{
    disclosures?: RegulationDisclosureItem[]
    projectCode?: string
  }>(),
  {
    disclosures: () => [],
    projectCode: '',
  },
)

const hasDisclosure = computed(() => props.disclosures.length > 0)
const visibleDisclosures = computed(() => props.disclosures.slice(0, 3))
const visibleCount = computed(() => visibleDisclosures.value.length)
const isSingleDisclosure = computed(() => visibleCount.value === 1)
const showCountBadge = computed(() => props.disclosures.length > 3)
const isDisclosureDialogOpen = ref(false)
const allDisclosures = computed(() => props.disclosures)

watch(isDisclosureDialogOpen, (isOpen) => {
  if (isOpen) {
    lockPageScroll()
    return
  }

  unlockPageScroll()
})

onBeforeUnmount(() => {
  unlockPageScroll()
})

const getDisclosureRoute = (item: RegulationDisclosureItem) =>
  localePath({
    name: 'disclosures-detail',
    params: {
      regulatorSlug: buildDisclosureRegulatorSlug(item.countryCode, item.regulatorName, item.countryName),
      exchangeSlug: routeExchangeAlias.value,
      slug: item.disclosureAlias,
    },
  })

const getTrader = (item: RegulationDisclosureItem) => item.traders?.[0] || null

const openDisclosureDialog = () => {
  if (!showCountBadge.value) return
  isDisclosureDialogOpen.value = true
}

const closeDisclosureDialog = () => {
  if (!isDisclosureDialogOpen.value) return
  isDisclosureDialogOpen.value = false
}

</script>

<template>
  <section
    v-if="hasDisclosure"
    class="regulation-disclosure-card flex w-full flex-col rounded-[12px] border border-[rgba(11,18,40,0.14)] bg-white px-[24px] pt-[24px] pb-[8px] max-[799px]:px-[16px] max-[799px]:pt-[20px]"
    aria-labelledby="exchange-regulation-disclosure-title"
  >
    <div class="flex items-center justify-between gap-[8px] pb-[16px] shadow-[inset_0_-0.5px_0_rgba(5,21,36,0.06)]">
      <h2
        id="exchange-regulation-disclosure-title"
        class="m-0 text-[20px] leading-[28px] font-semibold text-[#292a2e] max-[799px]:text-[18px] max-[799px]:leading-[26px]"
      >
        {{ t('82718') }}
      </h2>

      <button
        v-if="showCountBadge"
        type="button"
        class="regulation-disclosure-card-trigger inline-flex cursor-pointer items-center gap-[4px] border-0 bg-transparent p-0"
        @click="openDisclosureDialog"
      >
        <span
          class="regulation-disclosure-card-count inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#f8f8f8] px-[5px] text-[14px] leading-[22px] font-normal text-[#7d818a]"
        >
          {{ props.disclosures.length }}
        </span>
        <span class="regulation-disclosure-card-arrow block size-[16px]" aria-hidden="true"></span>
      </button>
    </div>

    <div
      class="regulation-disclosure-list grid grid-cols-3 gap-[24px] max-[799px]:grid-cols-1 max-[799px]:gap-[20px] max-[799px]:pt-[16px] min-[800px]:max-[1199px]:grid-cols-2"
      :class="[
        `regulation-disclosure-list-${visibleCount}`,
        {
          'regulation-disclosure-list-single ': isSingleDisclosure,
        },
      ]"
    >
      <NuxtLink
        v-for="item in visibleDisclosures"
        :key="item.disclosureId || `${item.disclosureType}-${item.disclosureDate}-${item.content}`"
        :to="getDisclosureRoute(item)"
        target="_blank"
        class="group regulation-disclosure-item flex min-w-0 flex-col gap-[12px]  pt-[20px] pb-[16px] hover:bg-[#f8f8f8] text-inherit no-underline"
        :class="{
          'w-full gap-[20px] max-[799px]:gap-[16px]': isSingleDisclosure,
        }"
      >
        <div
          class="regulation-disclosure-copy flex min-w-0 flex-1 flex-col gap-[8px]"
          :class="{
            'flex-none': isSingleDisclosure,
          }"
        >
          <p
            class="regulation-disclosure-type m-0 text-[16px] leading-[24px] font-semibold"
            :class="{
              'm-0': isSingleDisclosure,
            }"
            :style="{ color: item.disclosureTypeColor || '#E2483D' }"
          >
            {{ item.disclosureType || '--' }}
          </p>
          <p
            class="regulation-disclosure-content m-0 overflow-hidden text-[18px] leading-[26px] font-semibold text-ellipsis text-[#292a2e] transition-colors duration-200 max-[799px]:text-[16px] max-[799px]:leading-[24px]"
            :class="{
              'regulation-disclosure-content-single': isSingleDisclosure,
            }"
          >
            {{ item.content || '--' }}
          </p>
        </div>

        <div
          class="regulation-disclosure-meta flex min-w-0 flex-nowrap items-center gap-[8px]"
          :class="{ 'regulation-disclosure-meta-single': isSingleDisclosure }"
        >
          <template v-if="isSingleDisclosure">
            <div class="regulation-disclosure-single-meta-item flex min-w-0 flex-col gap-[10px] max-[799px]:gap-[6px]">
              <p class="m-0 text-[14px] leading-[22px] font-normal text-[#7d818a]"> {{ t('84557') }}</p>
              <div
                class="regulation-disclosure-single-meta-country m-0 flex min-w-0 items-center gap-[8px] overflow-hidden text-[16px] leading-[24px] font-medium whitespace-nowrap text-[#292a2e]"
              >
                <CommonImage
                  v-if="item.countryFlag"
                  class="regulation-disclosure-single-flag block h-[18px] w-[27px] flex-none rounded-[2px] object-cover"
                  :src="item.countryFlag"
                  :alt="item.countryName || item.regulatorName || ''"
                  loading="eager"
                  object-fit="cover"
                />
                <span class="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{{
                  item.countryName || '--'
                }}</span>
              </div>
            </div>

            <span class="block h-[38px] w-[1px] bg-[rgba(5,21,36,0.06)] max-[799px]:hidden" aria-hidden="true"></span>

            <div class="regulation-disclosure-single-meta-item flex min-w-0 flex-col gap-[10px] max-[799px]:gap-[6px]">
              <p class="m-0 text-[14px] leading-[22px] font-normal text-[#7d818a]">{{ t('84558') }}</p>
              <p class="m-0 flex min-w-0 items-center gap-[8px] text-[16px] leading-[24px] font-medium text-[#292a2e]">
                {{ item.disclosureDate || '--' }}
              </p>
            </div>

            <span class="block h-[38px] w-[1px] bg-[rgba(5,21,36,0.06)] max-[799px]:hidden" aria-hidden="true"></span>

            <div class="regulation-disclosure-single-meta-item flex min-w-0 flex-col gap-[10px] max-[799px]:gap-[6px]">
              <p class="m-0 text-[14px] leading-[22px] font-normal text-[#7d818a]">{{ t('84559') }}</p>
              <div
                class="regulation-disclosure-single-meta-brand m-0 flex min-w-0 items-center gap-[8px] overflow-hidden text-[16px] leading-[24px] font-medium whitespace-nowrap text-[#292a2e]"
              >
                <CommonImage
                  v-if="getTrader(item)?.icon"
                  class="regulation-disclosure-single-brand-icon block size-[20px] flex-none object-cover"
                  :src="getTrader(item)?.icon"
                  :alt="getTrader(item)?.traderName || ''"
                  loading="eager"
                  object-fit="cover"
                />
                <span class="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                  {{ getTrader(item)?.traderName || '--' }}
                </span>
              </div>
            </div>
          </template>

          <template v-else>
            <div
              v-if="getTrader(item)"
              class="regulation-disclosure-chip inline-flex min-w-0 flex-[0_1_auto] items-center gap-[4px] rounded-[4px] bg-[#f8f8f8] p-[8px]"
            >
              <CommonImage
                v-if="getTrader(item)?.icon"
                class="regulation-disclosure-chip-icon-round block size-[16px] flex-none rounded-[2px] object-cover"
                :src="getTrader(item)?.icon"
                :alt="getTrader(item)?.traderName || ''"
                loading="eager"
                object-fit="cover"
              />
              <span
                class="regulation-disclosure-chip-text min-w-0 overflow-hidden text-[12px] leading-[18px] font-normal text-ellipsis whitespace-nowrap text-[#505258]"
              >
                {{ getTrader(item)?.traderName || '--' }}
              </span>
            </div>

            <div
              v-if="item.countryFlag || item.regulatorName || item.countryName"
              class="regulation-disclosure-chip inline-flex min-w-0 flex-[0_2_auto] items-center gap-[4px] rounded-[4px] bg-[#f8f8f8] p-[8px]"
            >
              <CommonImage
                v-if="item.countryFlag"
                class="regulation-disclosure-chip-icon block h-[12px] w-[18px] flex-none object-cover"
                :src="item.countryFlag"
                :alt="item.countryName || item.regulatorName || ''"
                loading="eager"
                object-fit="cover"
              />
              <span
                class="regulation-disclosure-chip-text min-w-0 overflow-hidden text-[12px] leading-[18px] font-normal text-ellipsis whitespace-nowrap text-[#505258]"
              >
                {{ `${item.countryName || ''}${item.regulatorName || ''}` || '--' }}
              </span>
            </div>

            <span
              class="regulation-disclosure-date m-0 flex-none text-[12px] leading-[18px] font-normal text-[#8c8f97]"
            >
              {{ item.disclosureDate || '--' }}
            </span>
          </template>
        </div>
      </NuxtLink>
    </div>

    <Teleport to="body">
      <div
        v-if="isDisclosureDialogOpen"
        class="fixed inset-0 z-[1200] flex items-center justify-center bg-[rgba(11,18,40,0.36)] p-[24px] max-[799px]:p-[12px]"
        @click.self="closeDisclosureDialog"
      >
        <section
          class="w-[760px] max-w-[calc(100vw_-_48px)] overflow-hidden rounded-[28px] bg-white shadow-[0_24px_80px_rgba(8,19,62,0.18)] max-[799px]:max-w-[calc(100vw_-_24px)] max-[799px]:rounded-[24px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="regulation-disclosure-dialog-title"
        >
          <header
            class="flex items-center justify-between gap-[8px] px-[32px] pt-[32px] pb-[16px] shadow-[inset_0_-0.5px_0_rgba(5,21,36,0.06)] max-[799px]:px-[20px] max-[799px]:pt-[24px]"
          >
            <h2
              id="regulation-disclosure-dialog-title"
              class="m-0 overflow-hidden text-[20px] leading-[28px] font-semibold text-ellipsis whitespace-nowrap text-[#292a2e]"
            >
              {{ t('82718') }}
            </h2>
            <button
              type="button"
              class="relative inline-flex size-[32px] flex-none cursor-pointer items-center justify-center rounded-full border-0 bg-[#f3f5f8] p-0 hover:bg-[#eceff4]"
              aria-label="关闭监管披露弹窗"
              @click="closeDisclosureDialog"
            >
              <span class="regulation-disclosure-dialog-close-mark relative block size-[12px]"></span>
            </button>
          </header>

          <div
            class="max-h-[min(640px,calc(100vh_-_160px))] overflow-y-auto px-[32px] pt-[20px] pb-[32px] max-[799px]:max-h-[calc(100vh_-_120px)] max-[799px]:px-[20px] max-[799px]:pt-[16px] max-[799px]:pb-[24px]"
          >
            <article
              v-for="(item, index) in allDisclosures"
              :key="item.disclosureId || `${item.disclosureType}-${item.disclosureDate}-${index}`"
              class="py-[16px] shadow-[inset_0_-0.5px_0_rgba(5,21,36,0.06)] hover:bg-[#f8f8f8]"
              :class="{
                'regulation-disclosure-dialog-item-last': index === allDisclosures.length - 1,
              }"
            >
              <NuxtLink
                class="flex flex-col gap-[12px] text-inherit no-underline"
                :to="getDisclosureRoute(item)"
                target="_blank"
              >
                <div class="flex flex-col gap-[8px]">
                  <p
                    class="m-0 text-[16px] leading-[24px] font-semibold"
                    :style="{ color: item.disclosureTypeColor || '#E2483D' }"
                  >
                    {{ item.disclosureType || '--' }}
                  </p>
                  <p
                    class="regulation-disclosure-dialog-content m-0 overflow-hidden text-[18px] leading-[26px] font-semibold text-ellipsis text-[#292a2e] max-[799px]:text-[16px] max-[799px]:leading-[24px]"
                  >
                    {{ item.content || '--' }}
                  </p>
                </div>

                <div class="flex min-w-0 flex-wrap items-center gap-[8px]">
                  <div
                    v-if="getTrader(item)"
                    class="inline-flex min-w-0 items-center gap-[4px] rounded-[4px] bg-[#f8f8f8] p-[8px]"
                  >
                    <CommonImage
                      v-if="getTrader(item)?.icon"
                      class="regulation-disclosure-chip-icon-round block size-[16px] flex-none rounded-[2px] object-cover"
                      :src="getTrader(item)?.icon"
                      :alt="getTrader(item)?.traderName || ''"
                      loading="eager"
                      object-fit="cover"
                    />
                    <span
                      class="regulation-disclosure-chip-text min-w-0 overflow-hidden text-[12px] leading-[18px] font-normal text-ellipsis whitespace-nowrap text-[#505258]"
                    >
                      {{ getTrader(item)?.traderName || '--' }}
                    </span>
                  </div>

                  <div
                    v-if="item.countryFlag || item.regulatorName || item.countryName"
                    class="inline-flex min-w-0 items-center gap-[4px] rounded-[4px] bg-[#f8f8f8] p-[8px]"
                  >
                    <CommonImage
                      v-if="item.countryFlag"
                      class="regulation-disclosure-chip-icon block h-[12px] w-[18px] flex-none object-cover"
                      :src="item.countryFlag"
                      :alt="item.countryName || item.regulatorName || ''"
                      loading="eager"
                      object-fit="cover"
                    />
                    <span
                      class="regulation-disclosure-chip-text min-w-0 overflow-hidden text-[12px] leading-[18px] font-normal text-ellipsis whitespace-nowrap text-[#505258]"
                    >
                      {{ `${item.countryName || ''}${item.regulatorName || ''}` || '--' }}
                    </span>
                  </div>

                  <span
                    class="regulation-disclosure-date m-0 flex-none text-[12px] leading-[18px] font-normal text-[#8c8f97]"
                  >
                    {{ item.disclosureDate || '--' }}
                  </span>
                </div>
              </NuxtLink>
            </article>
          </div>
        </section>
      </div>
    </Teleport>
  </section>
</template>

<style scoped lang="less">
.regulation-disclosure-card-trigger:hover .regulation-disclosure-card-count {
  color: #1558bc;
}

.regulation-disclosure-card-trigger:hover .regulation-disclosure-card-arrow::before {
  border-color: #1558bc;
}

.regulation-disclosure-card-arrow::before {
  display: block;
  width: 6px;
  height: 6px;
  border-top: 1.5px solid #7d818a;
  border-right: 1.5px solid #7d818a;
  content: '';
  transform: translate(4px, 4px) rotate(45deg);
}

:dir(rtl) .regulation-disclosure-card-arrow::before {
  transform: translate(6px, 4px) rotate(-135deg);
}

.regulation-disclosure-list-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.regulation-disclosure-list-single {
  grid-template-columns: minmax(0, 1fr);
}

.regulation-disclosure-single-flag {
  width: 27px;
  height: 18px;
}

.regulation-disclosure-single-brand-icon {
  width: 20px;
  height: 20px;
}

.regulation-disclosure-chip {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  background: #f8f8f8;
  padding: 8px;
}

.regulation-disclosure-chip-icon {
  width: 18px;
  height: 12px;
}

.regulation-disclosure-chip-icon-round {
  width: 16px;
  height: 16px;
}

.regulation-disclosure-chip-text {
  min-width: 0;
  overflow: hidden;
  color: #505258;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.regulation-disclosure-date {
  color: #8c8f97;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.regulation-disclosure-meta-single {
  display: grid;
  width: 100%;
  grid-template-columns: minmax(0, 1fr) 1px minmax(0, 1fr) 1px minmax(0, 1fr);
  align-self: stretch;
  gap: 0;
}

.regulation-disclosure-meta-single > .regulation-disclosure-single-meta-item:not(:first-child) {
  padding-left: 32px;
}

.regulation-disclosure-meta-single > .regulation-disclosure-single-meta-item:not(:last-child) {
  padding-right: 32px;
}

:dir(rtl) .regulation-disclosure-meta-single > .regulation-disclosure-single-meta-item:first-child {
  padding-right: 0;
  padding-left: 32px;
}

:dir(rtl) .regulation-disclosure-meta-single > .regulation-disclosure-single-meta-item:last-child {
  padding-left: 0;
  padding-right: 32px;
}

.regulation-disclosure-content,
.regulation-disclosure-dialog-content {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.regulation-disclosure-dialog-close-mark::before,
.regulation-disclosure-dialog-close-mark::after {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 12px;
  height: 1.5px;
  border-radius: 999px;
  background: #7d818a;
  content: '';
  transform-origin: center;
}

.regulation-disclosure-dialog-close-mark::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.regulation-disclosure-dialog-close-mark::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

@media screen and (min-width: 800px) and (max-width: 1199px) {
  .regulation-disclosure-meta-single {
    gap: 20px;
  }
}

@media screen and (max-width: 799px) {
  .regulation-disclosure-meta-single {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }

  .regulation-disclosure-meta-single > .regulation-disclosure-single-meta-item:not(:first-child) {
    padding-left: 0;
  }

  .regulation-disclosure-meta-single > .regulation-disclosure-single-meta-item:not(:last-child) {
    padding-right: 0;
  }
}
</style>
